/* eslint-disable */
import propTypes from 'prop-types';
export { default as PropTypes } from 'prop-types';

const arrayWithLengthFactory = ({
  min = 0,
  max = Infinity,
  propType,
  isRequired
}) => (props, propSelector, // normally a propName, but when wrapped in arrayOf an index
componentName, location, propFullName // normally null but a string like "propName[index]" when wrapped in arrayOf
) => {
  const arr = props[propSelector];
  const propName = propFullName || propSelector;
  const baseMsg = "Invalid prop `".concat(propName, "` supplied to `").concat(componentName, "`,");
  const insideArrayOf = !!propFullName;

  if (isRequired && typeof arr === 'undefined') {
    return new Error("".concat(baseMsg, " this prop is required but no value was found."));
  }

  if (arr && !Array.isArray(arr)) {
    return new Error("".concat(baseMsg, " prop value is not an array."));
  }

  if (arr && arr.length > max) {
    return new Error("".concat(baseMsg, " array has a length of ").concat(arr.length, ", but the maximum is ").concat(max));
  }

  if (arr && arr.length < min) {
    return new Error("".concat(baseMsg, " array has a length of ").concat(arr.length, ", but the minimum is ").concat(min));
  }

  if (arr && propType) {
    const checkPropName = insideArrayOf ? location : propName;
    const checkPropType = insideArrayOf ? // array should be array containing only the given type
    propTypes.arrayOf(propTypes.arrayOf(propType)) : // array should contain only the given type
    propTypes.arrayOf(propType);
    const checkPropTypes = {
      [checkPropName]: checkPropType
    };
    const checkProps = insideArrayOf ? {
      [location]: props
    } : props; // When not inside an array, the error message only reads correctly
    // when using "prop"

    const checkProp = insideArrayOf ? propName : 'prop';
    propTypes.checkPropTypes(checkPropTypes, checkProps, checkProp, componentName);
  }

  return null;
};
/**
 * Ensure the prop value is an array with a length between a minimum and maximum.
 * If a third `propType` argument is passed each item in the array needs to be of that prop-type
 * @param {number} [min=0] - The minimal array length
 * @param {number} [max=Infinity] - The maximal array length
 * @param {function} [propType] - The prop-type that each array item needs to conform to
 * @return {Error|null} Returns null if all conditions are met, or an error
 * @example
 * import React from 'react'
 * import { arrayWithLength } from '@dhis2/prop-types'
 *
 * const LotsOfLists = props => <div {...props}>Does nothing</div>
 *
 * LotsOfLists.propTypes = {
 *     arrayWithMaxThreeNumbers: arrayWithLength(0, 3, propTypes.number),
 *     arrayWithAtLeastSixStrings: arrayWithLength(6, undefined, propTypes.string),
 *     arrayWithAtLeastTenItems: arrayWithLength(10),
 *     mandatoryArrayBetweenOneAndTen: arrayWithLength(1,10).isRequired,
 * }
 */


function arrayWithLength(min, max, propType) {
  const fn = arrayWithLengthFactory({
    min,
    max,
    propType,
    isRequired: false
  });
  fn.isRequired = arrayWithLengthFactory({
    min,
    max,
    propType,
    isRequired: true
  });
  return fn;
}

const conditionalFactory = (propsToPropType, isRequired) => (props, propName, componentName) => {
  const isDefined = typeof props[propName] !== 'undefined';

  if (typeof propsToPropType !== 'function') {
    return new Error("The `propsToPropType` argument passed to the `propsToPropTypeal` prop-validator was not a function.");
  }

  const propType = propsToPropType(props);

  if (typeof propType !== 'function') {
    return new Error("The response of `propsToPropType` call with the props was not a function.");
  } // Validation errors


  if (isRequired && !isDefined) {
    return new Error("Invalid prop `".concat(propName, "` supplied to `").concat(componentName, "`, this prop is required but no value was found."));
  }

  propTypes.checkPropTypes({
    [propName]: propType
  }, props, 'prop', componentName);
  return null;
};
/**
 * Uses either one or another propType, based on the result of the
 * propsToPropType callback, called with the props
 *
 * @param {Function} propsToPropType - A callback for determining which propType to use
 * @param {Function} eitherPropType
 * @param {Function} orPropType
 * @return {Error|null} Returns null if all propsToPropTypes are met, or an error
 *
 * @example
 * import React from 'react'
 * import { propsToPropTypeal } from '@dhis2/prop-types'
 *
 * const List = ({ multiple, selected, items }) => (
 *     const selectedItems = multiple ? selected : [selected]
 *
 *     <div>
 *         {items.map(item => (
 *             <li className={selectedItems.includes(item) ? 'active' : ''}>
 *                 {item}
 *             </li>
 *         ))}
 *     </div>
 * )
 *
 * List.propTypes = {
 *     multiple: propTypes.bool,
 *     items: props.arrayOf(prpoTypes.string),
 *     selected: propsToPropTypeal(
 *         props => propTypes.multiple
 *           ? propTypes.arrayOf(prpoTypes.string)
 *           : propTypes.string,
 *     ),
 * }
 */

const conditional = propsToPropType => {
  const fn = conditionalFactory(propsToPropType, false);
  fn.isRequired = conditionalFactory(propsToPropType, true);
  return fn;
};

const instanceOfComponentFactory = (Component, isRequired) => (props, propSelector, // normally a propName, but when wrapped in arrayOf an index
componentName, _location, propFullName // normally null but a string like "propName[index]" when wrapped in arrayOf
) => {
  const child = props[propSelector];
  const propName = propFullName || propSelector;
  const hasRenderableChild = child === 0 || !!child;
  const baseMsg = "Invalid prop `".concat(propName, "` supplied to `").concat(componentName, "`,");

  if (Array.isArray(child)) {
    return new Error("".concat(baseMsg, " expected a single component instance but received an array."));
  }

  if (!hasRenderableChild) {
    if (isRequired) {
      return new Error("".concat(baseMsg, " this is a required property but its value is `").concat(child, "`."));
    } else {
      return null;
    }
  }

  const expectedComponentName = typeof Component === 'string' ? Component : Component.name || Component.displayName;
  const foundComponentName = typeof child.type !== 'string' ? child.type && child.type.name ? child.type.name : child.type : child.type && (child.type.name || child.type.displayName);

  if (!foundComponentName) {
    return new Error("".concat(baseMsg, " could not read component name. Property value does not look like a component instance."));
  }

  if (child.type !== Component) {
    return new Error("".concat(baseMsg, " expected an instance of `").concat(expectedComponentName, "` but found an instance of `").concat(foundComponentName, "`."));
  }

  return null;
};
/**
 * Ensure the prop value is an instance of a certain component
 * @param {function|string} Component - The component that is expected. Can either be a React component,
 * or a string for built-in components, such as 'span', 'div', etc.
 * @return {Error|null} Returns null if all conditions are met, or an error
 * @example
 * import React from 'react'
 * import { instanceOfComponent } from '@dhis2/prop-types'
 * import { Button } from './Button'
 *
 * const ButtonWrap = ({ children }) => <div>{children}</div>
 * // This would allow the ButtonWrap to be empty
 * ButtonWrap.propTypes = {
 *     children: instanceOfComponent(Button)
 * }
 *
 * // Enforce presence of a Button instance
 * ButtonWrap.propTypes = {
 *     children: instanceOfComponent(Button).isRequired
 * }
 *
 * // Enforce presence of a multiple children, all Button instances
 * ButtonWrap.propTypes = {
 *     children: proptypes.arrayOf(instanceOfComponent(Button)).isRequired
 * }
 */


function instanceOfComponent(Component) {
  const fn = instanceOfComponentFactory(Component, false);
  fn.isRequired = instanceOfComponentFactory(Component, true);
  return fn;
}

const mutuallyExclusiveFactory = (exlusivePropNames, propType, isRequired) => (props, propName, componentName) => {
  const baseMsg = "Invalid prop `".concat(propName, "` supplied to `").concat(componentName, "`,");
  const isDefined = typeof props[propName] !== 'undefined'; // Usage errors

  if (exlusivePropNames.length === 0) {
    return new Error("mutuallyExclusive was called without any arguments for property `".concat(propName, "` on component `").concat(componentName, "`. Please add the required arguments."));
  } // Validation errors


  if (isRequired && !isDefined) {
    return new Error("".concat(baseMsg, " this prop is required but no value was found."));
  } // This is how to programatically invoke a propTypes check
  // https://github.com/facebook/prop-types#proptypescheckproptypes


  propTypes.checkPropTypes({
    [propName]: propType
  }, props, 'prop', componentName);

  if (props[propName]) {
    const thruthySiblingPropName = exlusivePropNames.find(name => props[name] && name !== propName);

    if (thruthySiblingPropName) {
      return new Error("".concat(baseMsg, " Property '").concat(propName, "' is mutually exclusive with '").concat(thruthySiblingPropName, "', but both have a thruthy value."));
    }
  }

  return null;
};
/**
 * Ensure that only one property within a specified list is thruthy
 * This function will also check if the current property value is of the specified type
 * @param {array<string>} exlusivePropNames - The prop names to be checked
 * @param {function} propType - The prop-type that the current prop-value needs to conform to
 * @return {Error|null} Returns null if all conditions are met, or an error
 * @example
 * import React from 'react'
 * import cx from 'classnames'
 * import propTypes from 'prop-types'
 * import { mutuallyExclusive } from '@dhis2/prop-types'
 *
 * const Alert = ({ danger, warning, success, children }) => (
 *     <div className={cx({danger, warning, success})}>
 *         {children}
 *     </div>
 * )
 *
 * const statusPropType = mutuallyExclusive(['danger', 'warning', 'success'], propTypes.bool)
 *
 * Alert.propTypes = {
 *     children: propTypes.node,
 *     danger: statusPropType,
 *     warning: statusPropType,
 *     success: statusPropType,
 * }
 */


function mutuallyExclusive(exlusivePropNames, propType) {
  const fn = mutuallyExclusiveFactory(exlusivePropNames, propType, false);
  fn.isRequired = mutuallyExclusiveFactory(exlusivePropNames, propType, true);
  return fn;
}

const isEmpty = value => typeof value === 'undefined' || value === null || value === '';

const requiredIfFactory = (condition, propType, isRequired) => (props, propName, componentName) => {
  const propValue = props[propName]; // Usage errors

  if (isRequired) {
    return new Error("Property `".concat(propName, "` on component `").concat(componentName, "` is using the `requiredIf` prop-validator combined with `.isRequired`. This is an invalid combination."));
  }

  if (typeof condition !== 'function') {
    return new Error("The `condition` argument passed to the `requiredIf` prop-validator was not a function.");
  }

  if (typeof propType !== 'function') {
    return new Error("The `propType` argument passed to the `requiredIf` prop-validator was not a function.");
  } // Validation errors


  if (condition(props) && isEmpty(propValue)) {
    return new Error("Invalid prop `".concat(propName, "` supplied to `").concat(componentName, "`, this prop is conditionally required but has value `").concat(propValue, "`."));
  } // This is how to programatically invoke a propTypes check
  // https://github.com/facebook/prop-types#proptypescheckproptypes


  propTypes.checkPropTypes({
    [propName]: propType
  }, props, 'prop', componentName);
  return null;
};
/**
 * Ensure the prop has a value (i.e. treat it as required) when a given sibling prop
 * also has a value, and ensure the prop is of the correct prop-type
 * @param {function} siblingPropName - The name of the sibling prop
 * @return {Error|null} Returns null if all conditions are met, or an error
 * @example
 * import React from 'react'
 * import { requiredIf } from '@dhis2/prop-types'
 *
 * const Test = ({ someBool, someString }) => (
 *     <div>
 *         <h1>someBool: {someBool ? 'true' : 'false'}</h1>
 *         <h1>someString: {someString}</h1>
 *     </div>
 * )
 * Test.propTypes = {
 *     someBool: propTypes.bool,
 *     someString: requiredIf(props => props.someBool, propTypes.string),
 * }
 */


function requiredIf(condition, propType) {
  const fn = requiredIfFactory(condition, propType, false);
  fn.isRequired = requiredIfFactory(condition, propType, true);
  return fn;
}



var customPropTypes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    arrayWithLength: arrayWithLength,
    conditional: conditional,
    instanceOfComponent: instanceOfComponent,
    mutuallyExclusive: mutuallyExclusive,
    requiredIf: requiredIf
});

export default customPropTypes;
export { arrayWithLength, conditional, instanceOfComponent, mutuallyExclusive, requiredIf };
//# sourceMappingURL=lib.js.map
